import { useEffect, useRef } from 'react';
import LangEnum from '../../../lang/LangEnum';
import { useLoadingStatusStore } from '../../../modules/context/loadingStatus/LoadingStatus.store';
import { LoadingStatus } from '../../../modules/context/loadingStatus/loadingStatus.store.types';
import { useTranslation } from '../../../modules/translation/TranslationContext';
import snackbarStore from '../../../stores/Snackbar.store';
import { SnackbarMessageType } from '../../../stores/snackbar.types';

const LOADING_STATUS_SNACKBAR_ID = 'loading-status';
const MIN_LOADING_NOTICE_MS = 1000;

interface LoadingStatusNotice {
  type: SnackbarMessageType;
  label: LangEnum;
  loading?: boolean;
}

const NOTICE_BY_STATUS: Record<LoadingStatus, LoadingStatusNotice> = {
  [LoadingStatus.LOADING]: { type: 'info', label: LangEnum.AUTOSAVE_SAVING, loading: true },
  [LoadingStatus.SAVED]: { type: 'success', label: LangEnum.AUTOSAVE_SAVED },
  [LoadingStatus.ERROR]: { type: 'error', label: LangEnum.AUTO_SAVE_ERROR_FORM_MESSAGE },
};

export const useLoadingStatusSnackbar = () => {
  const { translate } = useTranslation();
  const loadingShownAtRef = useRef(0);
  const pendingNoticeRef = useRef<ReturnType<typeof setTimeout>>();

  useEffect(() => {
    const unsubscribe = useLoadingStatusStore.subscribe((state, previous) => {
      if (state.loadingStatus === previous.loadingStatus) {
        return;
      }

      clearTimeout(pendingNoticeRef.current);

      const { label, ...notice } = NOTICE_BY_STATUS[state.loadingStatus];
      const showNotice = () =>
        snackbarStore.handleMessage({
          ...notice,
          id: LOADING_STATUS_SNACKBAR_ID,
          message: translate(label),
        });

      if (notice.loading) {
        loadingShownAtRef.current = Date.now();
        showNotice();
        return;
      }

      pendingNoticeRef.current = setTimeout(
        showNotice,
        Math.max(0, MIN_LOADING_NOTICE_MS - (Date.now() - loadingShownAtRef.current)),
      );
    });

    return () => {
      unsubscribe();
      clearTimeout(pendingNoticeRef.current);
    };
  }, [translate]);
};
